<template>
  <div>
    <b-card>
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h4 class="mb-0">Avisos</h4>
        <b-button variant="primary" size="sm" @click="abrirModal()">
          <i class="fa-solid fa-plus mr-50"></i> Nuevo Aviso
        </b-button>
      </div>

      <b-table
        :items="avisos"
        :fields="campos"
        responsive
        striped
        hover
        show-empty
        empty-text="No hay avisos"
        :busy="loading"
      >
        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner small></b-spinner>
          </div>
        </template>

        <template #cell(prioridad)="data">
          <b-badge :variant="variantePrioridad(data.value)">
            {{ data.value }}
          </b-badge>
        </template>

        <template #cell(destinatarios)="data">
          <span v-if="data.item.para_todos">
            <b-badge variant="info">Todos</b-badge>
          </span>
          <span v-else>
            {{ data.item.usuario_nombres || '-' }}
          </span>
        </template>

        <template #cell(lecturas)="data">
          <span v-if="data.item.lecturas && data.item.lecturas.length > 0">
            <b-badge variant="success" class="mr-25">{{ data.item.lecturas.filter(l => l.leido).length }}</b-badge>
            /
            <b-badge variant="secondary" class="ml-25">{{ data.item.lecturas.length }}</b-badge>
            <b-button variant="flat-info" size="sm" class="ml-50 p-0" @click="verLecturas(data.item)">
              <i class="fa-solid fa-eye"></i>
            </b-button>
          </span>
          <span v-else>-</span>
        </template>

        <template #cell(created_at)="data">
          {{ formatFecha(data.value) }}
        </template>

        <template #cell(acciones)="data">
          <b-button variant="flat-primary" size="sm" @click="abrirModal(data.item)" class="mr-50">
            <i class="fa-solid fa-pen"></i>
          </b-button>
          <b-button variant="flat-danger" size="sm" @click="eliminar(data.item.id)">
            <i class="fa-solid fa-trash"></i>
          </b-button>
        </template>
      </b-table>
    </b-card>

    <!-- Modal Crear/Editar -->
    <b-modal
      v-model="mostrarModal"
      :title="editando ? 'Editar Aviso' : 'Nuevo Aviso'"
      @ok.prevent="guardar"
      ok-title="Guardar"
      cancel-title="Cancelar"
      size="lg"
      no-close-on-backdrop
    >
      <b-form-group label="Titulo">
        <b-form-input v-model="form.titulo" placeholder="Titulo del aviso"></b-form-input>
      </b-form-group>

      <b-form-group label="Mensaje">
        <b-form-textarea v-model="form.mensaje" placeholder="Contenido del aviso" rows="4"></b-form-textarea>
      </b-form-group>

      <b-form-group label="Prioridad">
        <b-form-select v-model="form.prioridad" :options="opcionesPrioridad"></b-form-select>
      </b-form-group>

      <b-form-group label="Destinatarios">
        <b-form-checkbox v-model="form.para_todos" @change="onParaTodosChange">
          Enviar a todos los usuarios
        </b-form-checkbox>
      </b-form-group>

      <b-form-group v-if="!form.para_todos" label="Seleccionar usuarios">
        <b-form-checkbox-group v-model="form.usuario_ids" stacked>
          <b-form-checkbox
            v-for="u in usuarios"
            :key="u.id"
            :value="u.id"
          >
            {{ u.nombre }}
          </b-form-checkbox>
        </b-form-checkbox-group>
      </b-form-group>
    </b-modal>

    <!-- Modal Detalle Lecturas -->
    <b-modal
      v-model="mostrarModalLecturas"
      :title="'Lecturas: ' + avisoSeleccionado.titulo"
      ok-only
      ok-title="Cerrar"
      size="md"
    >
      <b-table
        :items="avisoSeleccionado.lecturas"
        :fields="camposLecturas"
        responsive
        small
        striped
        show-empty
        empty-text="Sin destinatarios"
      >
        <template #cell(leido)="data">
          <b-badge :variant="data.value ? 'success' : 'secondary'">
            {{ data.value ? 'Leido' : 'No leido' }}
          </b-badge>
        </template>
        <template #cell(leido_at)="data">
          {{ data.value ? formatFecha(data.value) : '-' }}
        </template>
      </b-table>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BTable, BBadge, BButton, BModal, BFormGroup, BFormInput,
  BFormTextarea, BFormSelect, BFormCheckbox, BFormCheckboxGroup, BSpinner
} from 'bootstrap-vue'
import axios from '@axios'

export default {
  components: {
    BCard, BTable, BBadge, BButton, BModal, BFormGroup, BFormInput,
    BFormTextarea, BFormSelect, BFormCheckbox, BFormCheckboxGroup, BSpinner
  },
  data() {
    return {
      avisos: [],
      usuarios: [],
      loading: false,
      mostrarModal: false,
      editando: false,
      editandoId: null,
      form: {
        titulo: '',
        mensaje: '',
        prioridad: 'info',
        para_todos: false,
        usuario_ids: []
      },
      avisoSeleccionado: { titulo: '', lecturas: [] },
      mostrarModalLecturas: false,
      campos: [
        { key: 'titulo', label: 'Titulo', sortable: true },
        { key: 'prioridad', label: 'Prioridad', sortable: true },
        { key: 'destinatarios', label: 'Destinatarios' },
        { key: 'lecturas', label: 'Leido' },
        { key: 'created_at', label: 'Fecha', sortable: true },
        { key: 'acciones', label: 'Acciones' }
      ],
      camposLecturas: [
        { key: 'usuario_nombre', label: 'Usuario' },
        { key: 'leido', label: 'Estado' },
        { key: 'leido_at', label: 'Fecha lectura' }
      ],
      opcionesPrioridad: [
        { value: 'info', text: 'Info' },
        { value: 'importante', text: 'Importante' },
        { value: 'urgente', text: 'Urgente' }
      ]
    }
  },
  methods: {
    async cargarAvisos() {
      this.loading = true
      try {
        const response = await axios.get('/avisos')
        this.avisos = response.data
      } catch (e) {
        console.error('Error al cargar avisos:', e)
      } finally {
        this.loading = false
      }
    },

    async cargarUsuarios() {
      try {
        const response = await axios.get('/usuarios')
        this.usuarios = response.data
      } catch (e) {
        console.error('Error al cargar usuarios:', e)
      }
    },

    abrirModal(aviso) {
      if (aviso) {
        this.editando = true
        this.editandoId = aviso.id
        this.form.titulo = aviso.titulo
        this.form.mensaje = aviso.mensaje
        this.form.prioridad = aviso.prioridad
        this.form.para_todos = aviso.para_todos === 1
        this.form.usuario_ids = aviso.usuario_ids ? aviso.usuario_ids.split(',').map(Number) : []
      } else {
        this.editando = false
        this.editandoId = null
        this.form = { titulo: '', mensaje: '', prioridad: 'info', para_todos: false, usuario_ids: [] }
      }
      this.mostrarModal = true
    },

    onParaTodosChange(val) {
      if (val) {
        this.form.usuario_ids = []
      }
    },

    async guardar() {
      if (!this.form.titulo || !this.form.mensaje) {
        this.$bvToast.toast('Titulo y mensaje son obligatorios', { title: 'Error', variant: 'danger', solid: true })
        return
      }

      if (!this.form.para_todos && this.form.usuario_ids.length === 0) {
        this.$bvToast.toast('Selecciona al menos un usuario o marca "Todos"', { title: 'Error', variant: 'danger', solid: true })
        return
      }

      try {
        if (this.editando) {
          await axios.put(`/avisos/${this.editandoId}`, this.form)
          this.$bvToast.toast('Aviso actualizado', { title: 'Avisos', variant: 'success', solid: true })
        } else {
          await axios.post('/avisos', this.form)
          this.$bvToast.toast('Aviso creado', { title: 'Avisos', variant: 'success', solid: true })
        }
        this.mostrarModal = false
        this.cargarAvisos()
      } catch (e) {
        console.error('Error al guardar aviso:', e)
        this.$bvToast.toast('Error al guardar aviso', { title: 'Error', variant: 'danger', solid: true })
      }
    },

    async eliminar(id) {
      const ok = await this.$bvModal.msgBoxConfirm('¿Eliminar este aviso?', {
        title: 'Confirmar',
        okVariant: 'danger',
        okTitle: 'Eliminar',
        cancelTitle: 'Cancelar'
      })
      if (!ok) return

      try {
        await axios.delete(`/avisos/${id}`)
        this.$bvToast.toast('Aviso eliminado', { title: 'Avisos', variant: 'success', solid: true })
        this.cargarAvisos()
      } catch (e) {
        console.error('Error al eliminar aviso:', e)
      }
    },

    verLecturas(aviso) {
      this.avisoSeleccionado = aviso
      this.mostrarModalLecturas = true
    },

    variantePrioridad(prioridad) {
      const map = { info: 'info', importante: 'warning', urgente: 'danger' }
      return map[prioridad] || 'secondary'
    },

    formatFecha(fecha) {
      if (!fecha) return '-'
      return new Date(fecha).toLocaleDateString('es-ES', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      })
    }
  },
  mounted() {
    this.cargarAvisos()
    this.cargarUsuarios()
  }
}
</script>
